import { Dialog, DialogContent, DialogProps } from '@material-ui/core';
import { FormSelectOrCreatePaymentMethodField } from 'app/main/settings/paymentMethods/forms/FormSelectOrCreatePaymentMethodField';
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { DialogTitleWithFormStepper } from 'framework/dialogs/DialogTitleWithFormStepper';
import { FormDatePicker } from 'framework/forms/FormDatePicker';
import { FormMoneyField } from 'framework/forms/FormMoneyField';
import { FormSingleCheckboxField } from 'framework/forms/FormSingleCheckboxField';
import { FormTextField } from 'framework/forms/FormTextField';
import { PageableFormDialogActions } from 'framework/forms/PageableFormDialogActions';
import { handleFormResponse } from 'framework/forms/utils/handleFormResponse';
import { useFormSubmit } from 'framework/hooks/useFormSubmit';
import { useSnackbarNotify } from 'framework/hooks/useSnackbarNotify';
import { ICashSaleRefundModel, ISale, paymentsForSalesCommand_newRefundForCashSale, paymentsQuery_nextReferenceForSale } from 'gen/ApiClient';
import { IStrings } from 'localization/IStrings';
import { useLocalization } from 'localization/useLocalization';
import React, { useCallback, useState } from 'react';
import { useNextReferenceInterceptor } from 'shared/interceptors/useNextReferenceInterceptor';
import { useNow } from 'shared/utils/useNow';
import * as yup from 'yup';

const createSchema = (strings: IStrings, max: number, min?: number) => {
	return yup
		.object<ICashSaleRefundModel>({
			referenceDate: yup.date().required(),
			isAutoSelectReference: yup.boolean().defined(),
			methodId: yup.string().required(),
			reference: yup.string(),
			value: yup.number().required(),
		})
		.defined();
};

const EmptyValues: ICashSaleRefundModel = {
	referenceDate: undefined as any,
	isAutoSelectReference: true,
	methodId: '',
	reference: '',
	value: 0,
};

const stepsRecord: Record<number, (keyof ICashSaleRefundModel)[]> = {
	0: ['isAutoSelectReference', 'reference', 'referenceDate'],
	1: ['methodId', 'value'],
};

interface IProps extends DialogProps {
	sale: ISale;
	confirm: (id: string) => void;
	cancel: VoidFunction;
}

export const RefundForCashSaleForm = ({ sale, confirm, cancel, ...rest }: IProps) => {
	const strings = useLocalization();
	const notify = useSnackbarNotify();
	const [create, isSubmitting] = useFormSubmit(paymentsForSalesCommand_newRefundForCashSale);
	const [step, setStep] = useState<number>(0);
	const now = useNow();

	const handleSubmit = async (values: ICashSaleRefundModel, helpers: FormikHelpers<ICashSaleRefundModel>) => {
		const r = await create(sale.id, values);
		if (handleFormResponse(r, helpers, stepsRecord, setStep)) {
			// navigate to detail?
			notify(strings.registeredWhat(strings.payment));
			confirm(r.result.objectId);
		}
	};

	if (now === undefined) {
		return <div></div>;
	}

	return (
		<Formik<ICashSaleRefundModel>
			validateOnMount
			initialValues={{
				...EmptyValues,
				value: sale.amountPaid,
				referenceDate: now,
			}}
			validationSchema={createSchema(strings, sale.amountPaid)}
			onSubmit={handleSubmit}>
			<Form>
				<InnerDialog
					step={step}
					setStep={setStep}
					cancel={cancel}
					isSubmitting={isSubmitting}
					sale={sale}
					{...rest}
				/>
			</Form>
		</Formik>
	);
};

interface IInnerDialogProps extends DialogProps {
	step: number;
	setStep: React.Dispatch<React.SetStateAction<number>>;
	cancel: VoidFunction;
	isSubmitting: boolean;
	sale: ISale;
}

const InnerDialog = ({ step, setStep, cancel, isSubmitting, sale, ...rest }: IInnerDialogProps) => {
	const props = useFormikContext<ICashSaleRefundModel>();
	const strings = useLocalization();
	const cb = useCallback(() => paymentsQuery_nextReferenceForSale(sale.id), [sale.id]);
	useNextReferenceInterceptor(cb, props);

	return (
		<Dialog {...rest}>
			<DialogTitleWithFormStepper
				title={`${strings.registerWhat(strings.refund)}: ${strings.sale} ${sale.reference}`}
				step={step}
				labels={[`${strings.date} & ${strings.reference}`, `${strings.method} & ${strings.amountAsValue}`]}
			/>
			<DialogContent
				className='df-col'
				dividers>
				{step === 0 && (
					<>
						<FormDatePicker<ICashSaleRefundModel>
							name='referenceDate'
							label={strings.referenceDate}
							disableFuture
							required
						/>
						<FormSingleCheckboxField<ICashSaleRefundModel>
							name='isAutoSelectReference'
							label={strings.autoSelectPaymentReferenceQuestion}
						/>
						<FormTextField<ICashSaleRefundModel>
							name='reference'
							label={strings.paymentReference}
							disabled={props.values.isAutoSelectReference}
						/>
					</>
				)}
				{step === 1 && (
					<>
						<FormSelectOrCreatePaymentMethodField<ICashSaleRefundModel>
							name='methodId'
							label={strings.paymentMethod}
							mode='sale'
						/>
						<FormMoneyField<ICashSaleRefundModel>
							name='value'
							label={strings.amountAsValue}
						/>
					</>
				)}
			</DialogContent>
			<PageableFormDialogActions
				step={step}
				setStep={setStep}
				cancel={cancel}
				isSubmitting={isSubmitting}
				submitText={strings.create}
				schema={createSchema(strings, sale.amountPaid)}
				stepsRecord={stepsRecord}
			/>
		</Dialog>
	);
};
